import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { AnchorButton } from 'components/Button/AnchorButton';
import { Button } from 'components/Button/Button';
import { Hide } from 'components/Hide';
import { Icon } from 'components/Icon';
import { LanguageSelectionMenu } from 'components/LanguageSelectionMenu';
import { TrackedLink } from 'components/Link';
import { IdealistLogo } from 'components/Logo/IdealistLogo';
import { PageFooter } from 'components/Page/PageFooter';
import { H4 } from 'components/Text/H4';
import { Text } from 'components/Text/Text';
import { setSearchFilter } from 'modules/main/store/ducks/mainSearch';
import { showModal } from 'modules/main/store/ducks/modal';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { colors, gutterWidth, transition } from 'theme';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { LanguageLocale } from 'utils/constants';
import { hasResumeDb } from 'utils/resumeDatabase';
import { getHomeUrl, listingSearchHome } from 'utils/searchHomePages';
import { pathnameEqual } from 'utils/url';

import { PageHeaderAccountDropdown } from './PageHeaderAccountDropdown';
import { PageHeaderAmpPostListingLink } from './PageHeaderAmpPostListingLink';
import { PAGE_HEADER_NAV, PAGE_HEADER_PATH_MAP } from './PageHeaderConstants';
import { PageHeaderContainer } from './PageHeaderContainer';
import { PageHeaderForOrganizations } from './PageHeaderForOrganizations';
import { PageHeaderMobileSectionTitle } from './PageHeaderMobileSectionTitle';
import { PageHeaderNavLink } from './PageHeaderNavLink';
import { PageHeaderNavLinks } from './PageHeaderNavLinks';
import { PageHeaderPostListingLink } from './PageHeaderPostListingLink';
import { PageHeaderSeparator } from './PageHeaderSeparator';
import { PageHeaderTopNavLink } from './PageHeaderTopNavLink';

const getNavOptions = (section: 'MAIN' | 'STAFF') =>
  section === 'STAFF' ? PAGE_HEADER_NAV.STAFF : PAGE_HEADER_NAV.MAIN;

const LoginLink = styled.a`
  color: ${colors.lightContentGrey};
  font-size: inherit;
  transition: color ${transition};
  &,
  &:hover,
  &:focus {
    text-decoration: none;
  }
  &:hover,
  &:focus {
    color: ${colors.brandBlue};
  }
`;

type Props = {
  pageUrl: Record<LanguageLocale, string | undefined>;
  minimalNav?: boolean;
  navSection?: 'MAIN' | 'STAFF';
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toggleSearch: (...args: Array<any>) => any;
  withSearchHero?: boolean;
  requestIsAmp?: boolean;
};

export function PageHeader({
  pageUrl,
  minimalNav,
  navSection = 'MAIN',
  toggleSearch,
  withSearchHero,
  requestIsAmp,
}: Props) {
  const userEnvironment = useUserEnvironment();
  const { user, isUserLoaded } = userEnvironment;
  const dispatch = useMainDispatch();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const redirectTo = location ? `${location.pathname}${location.search}` : '';
  const redirectParam = queryString.stringify({
    to: redirectTo,
  });
  const params = location ? queryString.parse(location.search) : {};
  const isStaff = navSection === 'STAFF';
  // TODO: Fix this the next time the file is edited.

  const fullSize = (withSearchHero || requestIsAmp) && !minimalNav;
  const isActive = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (type: any) => {
      const h = listingSearchHome(type);
      // staff
      return isStaff
        ? location &&
            type !== 'HOME' &&
            // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            (location.pathname.includes(PAGE_HEADER_PATH_MAP[type]) ||
              (type === 'QUEUES' && location.pathname.includes('staff/queues'))) // non-staff: grad program special-cased
        : (h && pathnameEqual(h, location.pathname)) || params.type === type;
    },
    [isStaff, location, params.type]
  );
  const navInactive = (
    isStaff ? PAGE_HEADER_NAV.STAFF : PAGE_HEADER_NAV.MAIN
  ).every((t) => !isActive(t));
  const nonAmpUrl = location.pathname.replace('/amp/', '/').replace(/#.+$/, '');
  const clickLogin = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      e.preventDefault();
      trackClicked('Log In Header');
      dispatch(
        showModal('LOGIN', {
          redirectTo,
          manualLogin: true,
        })
      );
    },
    [redirectTo, dispatch]
  );

  const closeSearch = () => setMenuOpen(false);

  useEffect(() => {
    if (location.hash === '#search') {
      toggleSearch(true);
    } else if (location.hash === '#menu') {
      setMenuOpen(true);
    }
  }, [location.hash, toggleSearch]);
  const mainLinks = useMemo(
    () => (
      <>
        {getNavOptions(navSection).map((type, index) => {
          const isSpanishBlog = type === 'BLOG' && CURRENT_LOCALE === 'es';
          const linkToPath =
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line no-nested-ternary
            type === 'HOME'
              ? '/' // TODO: Fix this the next time the file is edited.
              : // eslint-disable-next-line no-nested-ternary
              isStaff
              ? `/${
                  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                  PAGE_HEADER_PATH_MAP[type]
                    ? // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                      PAGE_HEADER_PATH_MAP[type]
                    : '/staff'
                }`
              : isSpanishBlog
              ? '/es/accion'
              : getHomeUrl(type as ListingType);

          // Grad Schools are the only header link that have a content page
          // Because we want to avoid a redirect after render when we visit a landing page,
          // pass state to avoid landing on the search page
          const linkTo =
            type !== 'GRAD_PROGRAM'
              ? linkToPath
              : {
                  pathname: linkToPath,
                  state: { redirectToContent: true },
                };

          return (
            <Box height="100%" key={type}>
              <PageHeaderTopNavLink
                navSection={navSection}
                index={index}
                linkTo={linkTo}
                isStaff={isStaff}
                type={type}
                // @ts-expect-error TS(2322): Type '(dispatch: Dispatch<MainStoreMainSearchActio... Remove this comment to see the full error message
                setSearchFilter={dispatch(setSearchFilter)}
                isActive={isActive}
                navInactive={navInactive}
                fullSize={fullSize}
                external={false}
              />
            </Box>
          );
        })}
      </>
    ),
    [navSection, isStaff, dispatch, isActive, navInactive, fullSize]
  );

  const mobileLogin = useMemo(
    () =>
      user ? (
        <AnchorButton href="/logout" size="large" variant="danger">
          {getText('Log Out')}
        </AnchorButton>
      ) : (
        <AnchorButton
          size="large"
          href={`/login?${redirectParam}`}
          onClick={clickLogin}
          data-qa-id="login-button-link"
          aria-label={getText('Log In')}
          variant="secondary"
        >
          <Hide lg>{getText('Log In')}</Hide>
          <Hide xs sm md xl>
            <Icon initialColorName="brandBlue" name="person" size={16} />
          </Hide>
        </AnchorButton>
      ),
    [clickLogin, user, redirectParam]
  );

  return (
    <PageHeaderContainer
      $fullSize={fullSize}
      position="relative"
      zIndex="101"
      height={[60, null, null, fullSize ? 72 : 40]}
    >
      <Box
        classes={['flex', 'align-center', 'justify-between']}
        position={['fixed', null, null, 'relative']}
        top={0}
        left={0}
        width={1}
        height={[60, null, null, fullSize ? 72 : 40]}
        px={gutterWidth / 2}
        bg="white"
        boxShadow={['0 2px 4px rgba(0,0,0,0.075)', null, null, 'none']}
        pointerEvents="auto"
      >
        <Box
          mr={24}
          flex="0 0 auto"
          // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'Display | u... Remove this comment to see the full error message
          display={[null, null, null, !fullSize && 'none']}
        >
          <TrackedLink
            href={isStaff ? '/staff' : '/'}
            title={`Idealist${isStaff ? ' Staff' : ''}`}
            eventName="Idealist Logo Header"
            data-qa-id="header-logo-link"
          >
            <div style={{ lineHeight: 0 }}>
              <IdealistLogo
                withText
                withStaffMarker={isStaff}
                height={{ mobile: 28, desktop: 36 }}
              />
            </div>
          </TrackedLink>
        </Box>

        <Hide lg xl>
          <Box ml="auto" classes={['flex', 'align-center']}>
            {requestIsAmp ? (
              <>
                <a
                  href={`${nonAmpUrl}#search`}
                  aria-label={getText('Toggle search')}
                  title={getText('Toggle search')}
                  data-qa-id="search-menu-button"
                >
                  <Icon
                    name="search"
                    size={22}
                    keepColorOnLoad
                    initialColorName="darkContentGrey"
                  />
                </a>

                <Box
                  as="a"
                  href={`${nonAmpUrl}#menu`}
                  aria-label={getText('Toggle menu')}
                  title={getText('Toggle menu')}
                  ml={16}
                  data-qa-id="mobile-menu-button"
                  variant="secondary"
                >
                  <Icon
                    name="hamburger"
                    size={22}
                    keepColorOnLoad
                    initialColorName="darkContentGrey"
                  />
                </Box>
              </>
            ) : (
              <>
                <Box>
                  <Button
                    onClick={toggleSearch}
                    aria-label={getText('Toggle search')}
                    title={getText('Toggle search')}
                    data-qa-id="search-menu-button"
                    variant="legacy-tertiary"
                  >
                    <Icon
                      initialColorName="mediumContentGrey"
                      name="search"
                      size={22}
                    />
                  </Button>
                </Box>

                <Box ml={16}>
                  <Button
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label={getText('Toggle menu')}
                    title={getText('Toggle menu')}
                    data-qa-id="mobile-menu-button"
                    variant="legacy-tertiary"
                  >
                    <Icon
                      initialColorName="mediumContentGrey"
                      name="hamburger"
                      size={22}
                    />
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Hide>

        <PageHeaderNavLinks
          flex="1 1 auto"
          $menuOpen={menuOpen}
          onClick={() => setMenuOpen(false)}
          height={[null, null, null, '100%']}
        >
          <Hide
            lg
            xl
            // @ts-expect-error TS(2322): Type '{ children: Element; lg: true; xl: true; hei... Remove this comment to see the full error message
            height={[null, null, null, '100%']}
          >
            <Box
              classes={['flex', 'align-center', 'justify-between']}
              px={gutterWidth / 2}
              height={60}
              width="100%"
              borderBottom={`1px solid ${colors.selectionGrey}`}
            >
              <H4 mb={0} color={colors.brandBlue}>
                {user
                  ? /* TRANSLATORS: user greeting at top of page */
                    sprintf(getText('Hi, %(name)s!'), {
                      name: user.firstName || user.email,
                    })
                  : getText('Menu')}
              </H4>

              <Box ml="auto">
                <Button
                  onClick={closeSearch}
                  aria-label={getText('Close search')}
                  variant="secondary"
                >
                  <Icon
                    name="close"
                    size={16}
                    keepColorOnLoad
                    initialColorName="brandBlue"
                  />
                </Button>
              </Box>
            </Box>
          </Hide>

          <Box
            display={['block', null, null, 'flex']}
            height={[null, null, null, '100%']}
            justifyContent="space-between"
            p={[gutterWidth / 2, null, null, 0]}
            pb={[0, null, null, 0]}
          >
            <Box
              display={['block', null, null, 'flex']}
              alignItems="center"
              mt="1px"
            >
              {mainLinks}
            </Box>

            <Box
              display={['block', null, null, 'flex']}
              alignItems="center"
              ml="auto"
              mt={[0, null, null, '1px']}
            >
              {user ? (
                <>
                  <Hide lg xl>
                    <PageHeaderMobileSectionTitle>
                      {getText('Settings')}
                    </PageHeaderMobileSectionTitle>

                    <PageHeaderNavLink
                      isStaff={isStaff}
                      type="USER_DASHBOARD"
                      to="/dashboard/email-alerts"
                    >
                      {getText('Email Alerts')}
                    </PageHeaderNavLink>

                    {hasResumeDb(userEnvironment) && (
                      <PageHeaderNavLink
                        isStaff={isStaff}
                        type="USER_DASHBOARD"
                        to="/dashboard/employment-profile"
                      >
                        {getText('Employment Profile')}
                      </PageHeaderNavLink>
                    )}

                    <PageHeaderNavLink
                      isStaff={isStaff}
                      type="USER_DASHBOARD"
                      to="/dashboard/account-settings"
                    >
                      {getText('My Account')}
                    </PageHeaderNavLink>

                    {user.orgs.length > 0 && (
                      <>
                        <PageHeaderMobileSectionTitle>
                          {getText('My Organizations')}
                        </PageHeaderMobileSectionTitle>

                        {user.orgs.map((org) => (
                          <Box key={org.id}>
                            <PageHeaderNavLink
                              isStaff={isStaff}
                              type="ORG_DASHBOARD"
                              to={
                                org.hasGradProgram
                                  ? `/admin/${org.id}/grad-program`
                                  : `/admin/${org.id}`
                              }
                            >
                              {org.name}
                            </PageHeaderNavLink>
                          </Box>
                        ))}
                      </>
                    )}
                  </Hide>

                  <Hide xs sm md>
                    <Box mt="1px">
                      <PageHeaderAccountDropdown isStaff={isStaff} />
                    </Box>
                  </Hide>

                  <PageHeaderSeparator />
                </>
              ) : (
                <Box display={['none', null, null, 'flex']} alignItems="center">
                  <LoginLink
                    href={`/login?${redirectParam}`}
                    onClick={clickLogin}
                    data-qa-id="login-link"
                    aria-label={getText('Log In')}
                  >
                    {(isUserLoaded || requestIsAmp) && getText('Log In')}
                  </LoginLink>
                  <PageHeaderSeparator ml={12} />
                </Box>
              )}

              <Hide xs sm md>
                {requestIsAmp ? (
                  <Box
                    component={LoginLink}
                    mx={12}
                    classes={['flex', 'align-center']}
                    href={`${nonAmpUrl}#search`}
                    aria-label={getText('Toggle search')}
                    title={getText('Toggle search')}
                    data-qa-id="search-menu-button"
                    variant="secondary"
                  >
                    <Box mr="6px">
                      <Icon
                        name="search"
                        size={18}
                        initialColorName="lightContentGrey"
                      />
                    </Box>
                    {getText('Search')}
                  </Box>
                ) : (
                  <Box mt="1px">
                    <LanguageSelectionMenu
                      // @ts-expect-error TS(2322): Type 'Record<"en" | "es" | "pt", string | undefine... Remove this comment to see the full error message
                      pageUrl={pageUrl}
                    />
                  </Box>
                )}
              </Hide>

              {!isStaff && (
                <>
                  <PageHeaderForOrganizations />
                  <PageHeaderSeparator mr={12} />
                  <PageHeaderMobileSectionTitle />

                  {requestIsAmp ? (
                    <PageHeaderAmpPostListingLink />
                  ) : (
                    <PageHeaderPostListingLink />
                  )}
                  <Hide lg xl>
                    <PageHeaderNavLink
                      to="/organizations/create"
                      data-qa-id="create-org"
                      onClick={() => {
                        trackClicked('Add Your Organization');
                      }}
                      type="ORG"
                    >
                      <Box classes={['flex', 'align-center']}>
                        <Box mr={[14, null, null, '6px']} mt="-4px">
                          <Icon
                            initialColorName="mediumContentGrey"
                            name="circle-add"
                            size={18}
                          />
                        </Box>
                        <Text fontSize="inherit" fontWeight="normal">
                          {getText('Add Your Organization')}
                        </Text>
                      </Box>
                    </PageHeaderNavLink>

                    <PageHeaderMobileSectionTitle />

                    {mobileLogin}

                    <Box
                      width={`calc(100% + ${gutterWidth}px)`}
                      ml={-gutterWidth / 2}
                      mt={gutterWidth}
                      borderBottom={`6px solid ${colors.brandBlue}`}
                    >
                      <PageFooter url={pageUrl} inPageHeader />
                    </Box>
                  </Hide>
                </>
              )}
            </Box>
          </Box>
        </PageHeaderNavLinks>
      </Box>
    </PageHeaderContainer>
  );
}
